import type { AiOnboardingDocumentType } from '@pharmaplan/common';
import * as Yup from 'yup';

import strings from '../../../localization';

export const MAXIMUM_UPLOAD_FILE_SIZE_IN_KB = 2000;

const IMAGE_FILE_EXTENSIONS = ['.jpeg', '.jpg', '.png'];
const DOCUMENT_FILE_EXTENSIONS = [
  '.doc',
  '.docx',
  '.jpeg',
  '.jpg',
  '.pdf',
  '.png',
];
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const DOCUMENT_MIME_TYPES = [
  ...IMAGE_MIME_TYPES,
  'application/msword',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export interface IDocumentUploadConfig {
  acceptedFileTypes: string;
  extensions: string[];
  fileType: 'all' | 'image';
  message: string;
  mimeTypes: string[];
  title: string;
}

export const documentUploadConfigs: Record<
AiOnboardingDocumentType,
IDocumentUploadConfig
> = {
  profilePicture: {
    acceptedFileTypes: IMAGE_FILE_EXTENSIONS.join(','),
    extensions: IMAGE_FILE_EXTENSIONS,
    fileType: 'image',
    message: strings.sizeOfImage,
    mimeTypes: IMAGE_MIME_TYPES,
    title: strings.uploadYourPicture,
  },
  resume: {
    acceptedFileTypes: DOCUMENT_FILE_EXTENSIONS.join(','),
    extensions: DOCUMENT_FILE_EXTENSIONS,
    fileType: 'all',
    message: strings.formatString(
      strings.fileValidationFormat,
      strings.resume,
    ) as string,
    mimeTypes: DOCUMENT_MIME_TYPES,
    title: strings.uploadYourResume,
  },
  diplomaCertificate: {
    acceptedFileTypes: DOCUMENT_FILE_EXTENSIONS.join(','),
    extensions: DOCUMENT_FILE_EXTENSIONS,
    fileType: 'all',
    message: strings.formatString(
      strings.fileValidationFormat,
      strings.diploma,
    ) as string,
    mimeTypes: DOCUMENT_MIME_TYPES,
    title: strings.uploadYourDiploma,
  },
};

export const isFile = (value: unknown): value is File =>
  typeof File !== 'undefined' && value instanceof File;

const getFileExtension = (fileName: string) => {
  const extensionStart = fileName.lastIndexOf('.');
  return extensionStart >= 0
    ? fileName.slice(extensionStart).toLowerCase()
    : '';
};

export const createDocumentFileSchema = (
  config: IDocumentUploadConfig,
) =>
  Yup.mixed<File>()
    .required(strings.requiredField)
    .test(
      'file-instance',
      strings.requiredField,
      isFile,
    )
    .test(
      'file-size',
      config.message,
      (value) =>
        isFile(value)
        && value.size / 1024 <= MAXIMUM_UPLOAD_FILE_SIZE_IN_KB,
    )
    .test(
      'file-type',
      strings.formatString(
        strings.pleaseEnterValidFormat,
        strings.uploadedFile.toLowerCase(),
      ) as string,
      (value) =>
        isFile(value)
        && (
          config.mimeTypes.includes(value.type)
          || config.extensions.includes(getFileExtension(value.name))
        ),
    );
